import { useState, useEffect } from 'react'
import { ProgressSteps } from './progress-steps'
import { ActionBar } from './action-bar'
import { lifeEventApi } from './life-event-api'

const CATEGORY_MAP = {
  1: 'early-life',
  2: 'education',
  3: 'career',
  4: 'relationship',
  5: 'life-events'
}

const CATEGORY_LABELS = {
  'early-life': 'Early Life',
  'education': 'Education',
  'career': 'Career',
  'relationship': 'Relationships',
  'life-events': 'Life Changes'
}

export function LifeEventGeneric({ 
  eventData, 
  formData, 
  onFormDataChange,
  onEventSelect,
  templates,
  isLoading
}) {
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [showForms, setShowForms] = useState(false)

  // Reset everything when category changes
  function handleCategorySelect(categoryId) {
    setSelectedCategory(categoryId)
    setShowForms(false)
    onEventSelect(null) // Reset selected event
    onFormDataChange({}) // Reset form data
  }

  function handleEventSelect(template) {
    onEventSelect(template)
    setShowForms(true)
  }

  return (
    <div className="space-y-8">
      {/* Category Pills */}
      <div className="flex space-x-4 mb-8">
        {[
          { id: 1, label: 'Early Life' },
          { id: 2, label: 'Education' },
          { id: 3, label: 'Career' },
          { id: 4, label: 'Relationship' },
          { id: 5, label: 'Life Events' }
        ].map(({ id, label }) => (
          <button
            key={id}
            onClick={() => handleCategorySelect(id)}
            className={`px-6 py-2 rounded-full text-sm transition-colors
              ${selectedCategory === id
                ? 'bg-[#556B2F] text-white' 
                : 'bg-white/90 text-gray-600 hover:bg-white'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Event Cards - Only show if category is selected */}
      {selectedCategory && (
        <div className="grid grid-cols-3 gap-6">
          {templates
            .filter(template => template.category === selectedCategory)
            .map(template => (
              <div 
                key={template.id}
                onClick={() => handleEventSelect(template)}
                className={`bg-white/90 rounded-lg p-6 transition-colors cursor-pointer border
                  ${eventData?.id === template.id 
                    ? 'border-[#556B2F] bg-white' 
                    : 'border-transparent hover:border-[#556B2F] hover:bg-white'}`}
              >
                <div className="mb-4 text-2xl">
                  {template.category === 1 && '👶'}
                  {template.category === 2 && '🎓'}
                  {template.category === 3 && '💼'}
                  {template.category === 4 && '❤️'}
                  {template.category === 5 && '🌟'}
                </div>
                <h3 className="text-lg font-semibold mb-2">{template.name}</h3>
                <p className="text-gray-600 text-sm">{template.description}</p>
              </div>
            ))}
        </div>
      )}

      {/* Forms - Only show if event is selected */}
      {showForms && eventData && (
        <div className="space-y-8">
          {/* Basic Information Section */}
          <div>
            <h2 className="text-xl font-semibold mb-6">Basic Information</h2>
            <div className="bg-white/90 rounded-lg p-8">
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                {Object.entries(eventData.required_fields).map(([fieldName, field]) => (
                  <div key={fieldName} className="space-y-2">
                    <label className="block text-sm text-gray-700">
                      {field.label} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type={field.type === 'date' ? 'date' : 'text'}
                      className="w-full p-2 border rounded-md bg-gray-50"
                      placeholder={field.placeholder || ''}
                      onChange={(e) => onFormDataChange({ 
                        ...formData, 
                        [fieldName]: e.target.value 
                      })}
                      value={formData[fieldName] || ''}
                      required
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Story & Details Section */}
          {eventData.story_prompts?.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold mb-6">Story & Details</h2>
              <div className="bg-white/90 rounded-lg p-8">
                <h3 className="text-lg font-semibold mb-6">Share Your Story</h3>
                <div className="space-y-6">
                  {eventData.story_prompts.map((prompt, index) => (
                    <div key={index} className="space-y-2">
                      <label className="block text-sm text-gray-700">
                        {prompt}
                      </label>
                      <textarea 
                        className="w-full p-3 border rounded-md h-32 resize-none bg-gray-50"
                        onChange={(e) => onFormDataChange({
                          ...formData,
                          story_details: {
                            ...(formData.story_details || {}),
                            [prompt]: e.target.value
                          }
                        })}
                        value={formData.story_details?.[prompt] || ''}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
} 